import { formatDrfErrorMessage } from '@/utils/alerts'

/** Matches Django status payload sample size (import_job_failures.IMPORT_JOB_ERRORS_SAMPLE_MAX).
 * Full failures: GET /import-jobs/<id>/failures/ and /failures/export/.
 */
export const IMPORT_JOB_ERRORS_SAMPLE_MAX = 100

/**
 * Errors to render in UI (API may return up to IMPORT_JOB_ERRORS_SAMPLE_MAX).
 * @param {Array<unknown>|null|undefined} errorsSample
 * @returns {Array<Record<string, unknown>>}
 */
export function importJobErrorsForDisplay(errorsSample) {
  if (!Array.isArray(errorsSample)) return []
  return errorsSample.slice(0, IMPORT_JOB_ERRORS_SAMPLE_MAX)
}

/**
 * Readable message from ImportJob.errors_sample row (normalized or legacy).
 * @param {unknown} err
 * @returns {string}
 */
export function formatImportJobErrorMessage(err) {
  if (!err || typeof err !== 'object') {
    return String(err ?? 'Unknown error')
  }
  if (typeof err.message === 'string' && err.message.trim()) {
    return err.message.trim()
  }
  const legacy = err.error
  if (typeof legacy === 'string' && legacy.trim()) {
    if (legacy.includes('ErrorDetail(string=')) {
      const m = legacy.match(/ErrorDetail\(string=['"](.+?)['"]/)
      if (m) return m[1].replace(/\\'/g, "'").replace(/\\"/g, '"')
    }
    return legacy.trim()
  }
  if (legacy && typeof legacy === 'object') {
    const fromDrf = formatDrfErrorMessage(legacy)
    if (fromDrf) return fromDrf
    try {
      return JSON.stringify(legacy)
    } catch {
      return 'Unknown error'
    }
  }
  return 'Unknown error'
}

/**
 * One traceable line: row + SKU + name + message.
 * @param {Record<string, unknown>} err
 * @returns {string}
 */
export function formatImportJobErrorLine(err) {
  const message = formatImportJobErrorMessage(err)
  const sku = err?.sku != null ? String(err.sku).trim() : ''
  const name = err?.name != null ? String(err.name).trim() : ''
  const row =
    err?.index != null && Number.isFinite(Number(err.index))
      ? `Row ${Number(err.index) + 1}`
      : ''
  const who = [sku, name].filter(Boolean).join(' · ')
  const head = [row, who].filter(Boolean).join(' — ')
  return head ? `${head}: ${message}` : message
}

/**
 * @param {Record<string, unknown>} err
 * @returns {{ row: string, sku: string, name: string, code: string, message: string }}
 */
export function importJobErrorParts(err) {
  const index = err?.index
  return {
    row:
      index != null && Number.isFinite(Number(index))
        ? `Row ${Number(index) + 1}`
        : '',
    sku: err?.sku != null ? String(err.sku).trim() : '',
    name: err?.name != null ? String(err.name).trim() : '',
    code: err?.code != null ? String(err.code).trim() : '',
    message: formatImportJobErrorMessage(err),
  }
}
